export type WebAppDisplayMode = 'standalone' | 'minimal-ui' | 'fullscreen' | 'browser' | 'unknown';

export type WebAppEvent = 'web_app_open' | 'web_app_resume' | 'web_app_display_mode_change'
	| 'code_reader_view' | 'code_submit' | 'code_resolved';

export type CodeEntrySource = 'menu_preview' | 'bottom_navigation' | 'side_navigation'
	| 'home_link' | 'internal_link' | 'direct_or_unknown' | 'code_link';
export type CodeInputMethod = 'internal_camera' | 'manual' | 'direct_link';
export type WebAppEntrySource = 'code_link' | 'foorban_com' | 'internal_link'
	| 'external_referral' | 'direct_or_unknown';

const entrySources: CodeEntrySource[] = [
	'menu_preview', 'bottom_navigation', 'side_navigation', 'home_link',
	'internal_link', 'direct_or_unknown', 'code_link',
];

/** A browser visit cannot tell us whether the PWA is installed elsewhere. */
export function getWebAppDisplayMode(): WebAppDisplayMode {
	if ((window.navigator as Navigator & { standalone?: boolean }).standalone === true) {
		return 'standalone';
	}
	if (typeof window.matchMedia !== 'function') {
		return 'unknown';
	}
	const modes: WebAppDisplayMode[] = ['standalone', 'minimal-ui', 'fullscreen', 'browser'];
	return modes.find(mode => window.matchMedia(`(display-mode: ${mode})`).matches) || 'unknown';
}

/** Classify the initial document entry, before routing/login can change its URL.
 * This describes the entry route/referrer, independently of the display mode.
 * A code link is not proof that the phone camera opened it; an empty referrer
 * cannot distinguish typing an address, bookmarks and links hiding the referrer.
 */
export function getWebAppEntrySource(): WebAppEntrySource {
	if (/^\/code-landing\/[^/]+\/?$/.test(window.location.pathname)) {
		return 'code_link';
	}
	if (!document.referrer) return 'direct_or_unknown';
	try {
		const referrer = new URL(document.referrer);
		if (referrer.protocol !== 'https:' && referrer.protocol !== 'http:') {
			return 'direct_or_unknown';
		}
		if (referrer.hostname === 'foorban.com' || referrer.hostname === 'www.foorban.com') {
			return 'foorban_com';
		}
		return referrer.origin === window.location.origin ? 'internal_link' : 'external_referral';
	} catch {
		return 'direct_or_unknown';
	}
}

/** State belongs to this history entry, not to a sticky session-wide flag. */
export function codeNavigationState(link: string, source: CodeEntrySource) {
	return /^\/code\/?(?:[?#]|$)/.test(link) ? { codeEntrySource: source } : undefined;
}

export function getCodeEntrySource(state: unknown, hasCodeInUrl: boolean): CodeEntrySource {
	if (hasCodeInUrl) {
		// A URL can come from a camera, NFC, a bookmark or a shared link.
		return 'code_link';
	}
	const source = state && typeof state === 'object'
		? (state as { codeEntrySource?: CodeEntrySource }).codeEntrySource : undefined;
	return source && entrySources.includes(source) ? source : 'direct_or_unknown';
}

type EventParameters = Record<string, string | number | boolean>;

/** One atomic push so GTM reads the parameters belonging to this event. */
export function trackWebAppEvent(event: WebAppEvent, parameters: EventParameters = {}) {
	try {
		window['dataLayer'] = window['dataLayer'] || [];
		window['dataLayer'].push({
			entry_source: undefined,
			scan_method: undefined,
			destination_type: undefined,
			...parameters,
			event,
			web_version: 'v1',
			display_mode: getWebAppDisplayMode(),
		});
	} catch {
		// Analytics must never prevent scanning or buying.
	}
}

/** Give GTM time to send the event before a full-page navigation, with a hard fallback. */
export function trackCodeResolved(parameters: EventParameters, navigate: () => void, external: boolean) {
	if (!external) {
		trackWebAppEvent('code_resolved', parameters);
		navigate();
		return;
	}
	let completed = false;
	let timeout: number;
	const finish = () => {
		if (!completed) {
			completed = true;
			window.clearTimeout(timeout);
			navigate();
		}
	};
	timeout = window.setTimeout(finish, 350);
	try {
		window['dataLayer'] = window['dataLayer'] || [];
		window['dataLayer'].push({
			entry_source: undefined,
			scan_method: undefined,
			destination_type: undefined,
			...parameters,
			event: 'code_resolved',
			web_version: 'v1',
			display_mode: getWebAppDisplayMode(),
			eventCallback: finish,
			eventTimeout: 300,
		});
	} catch {
		finish();
	}
}

export function startWebAppTracking(): () => void {
	trackWebAppEvent('web_app_open', { entry_source: getWebAppEntrySource() });
	// Installed apps may remain open in the background for days without reloading.
	let wasHidden = document.visibilityState === 'hidden';
	const onVisibilityChange = () => {
		if (wasHidden && document.visibilityState === 'visible') {
			trackWebAppEvent('web_app_resume');
		}
		wasHidden = document.visibilityState === 'hidden';
	};
	document.addEventListener('visibilitychange', onVisibilityChange);
	let lastMode = getWebAppDisplayMode();
	const onChange = () => {
		const mode = getWebAppDisplayMode();
		if (mode !== lastMode) {
			lastMode = mode;
			trackWebAppEvent('web_app_display_mode_change');
		}
	};
	const queries = typeof window.matchMedia === 'function'
		? ['standalone', 'minimal-ui', 'fullscreen', 'browser'].map(mode => window.matchMedia(`(display-mode: ${mode})`))
		: [];
	queries.forEach(query => {
		if (query.addEventListener) {
			query.addEventListener('change', onChange);
		} else if (query.addListener) {
			query.addListener(onChange);
		}
	});
	return () => {
		document.removeEventListener('visibilitychange', onVisibilityChange);
		queries.forEach(query => {
			if (query.removeEventListener) {
				query.removeEventListener('change', onChange);
			} else if (query.removeListener) {
				query.removeListener(onChange);
			}
		});
	};
}
